/** Registry DI for the utils and tools adapter/logging surfaces: entry points call `configureRegistry()` at startup and the lazy accessors resolve the CURRENT slot per call, so module-load ordering is safe. Unconfigured (direct-node scripts), the console fallbacks never throw and never silently drop; they now apply a DEEP scrub (see `scrubFallbackArg`) rather than the string-only one they carried before 2026-08-07, but they still stop short of the configured secure-logger -- so entry points MUST configure the real loggers. */

import { redactTelemetryPayload } from '@federa/const/telemetry-redaction';
import { BROWSER_DEBUG_LOG_STORAGE_KEY, LOGGER_TAGS, LOG_LEVELS } from '@federa/const/logging';
import { hasAnyKeys } from '@federa/const/record-guards';
import { documentLocalStorage } from '@federa/const/shared-window-state';
import type { PropertyOpsLogger } from '@federa/types/properties-details';
import type { SecureLogger } from '@federa/types/api-redaction';
import type { ScopedLogger, StandardLogFields } from '@federa/types/observability-scoped-logger';
import type { LogActivityContextArg } from '@federa/types/activity';
import type { ActivityBase } from '@federa/types/user-activity';

/** Sentinel id returned by unconfigured activity-logging fallbacks (mirrors defaultPropertyOpsLogger's sentinel pattern). */
const UNCONFIGURED_SENTINEL = 'unconfigured-registry';

/**
 * DEEP-scrub one console-fallback arg.
 *
 * WHAT WAS WRONG WITH THE OLD ONE. It tested `typeof arg === 'string'` and passed everything else through, so an
 * OBJECT arg -- which is what a logger is normally handed -- printed verbatim: `{ headers: { authorization: 'Bearer
 * ...' } }` reached the console untouched, and the `logError` / `logActivity` fallbacks below pass exactly such bags.
 * A fallback weaker than the thing it substitutes for is a silent hole, and the hole was in the common shape.
 *
 * WHY `redactTelemetryPayload`. The layer floor forecloses the obvious answer: this is a `utils/` leaf, so it may
 * value-import only `@federa/const`, `@federa/types` and `zod` (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist),
 * which rules out `combinedRedact` (`tools/observability/secure-logger.ts`). It does not rule out `redactPII`, which is
 * const-homed (`const/pii-redaction.ts`) and reachable from here, but it is NOT adopted: this leaf's console fallback is
 * a last-resort path and widening what it scrubs is the same
 * false-positive question `const/telemetry-redaction.ts` measures for its own projection, owned there rather than
 * decided here. `redactTelemetryPayload` is const-homed too and remains the path this leaf takes: a structural walk with
 * the `SENSITIVE_KEY_PATTERNS` + `SENSITIVE_FIELDS` key screen, `scrubCredentialSpans` and email-span scrubbing on
 * every string, and depth / array / key / length caps that also bound a runaway console line.
 *
 * RESIDUAL vs the configured logger, stated rather than implied: the `PII_PATTERNS` VALUE shapes (phone, SSN, card,
 * address) are not applied on this path, so those still print. The relocation half of the recorded cure LANDED
 * 2026-08-24 (`utils/security/pii-redaction.ts` -> `const/pii-redaction.ts`, a real SSOT move rather than a copy), so
 * the reason is no longer that no const-homed consumer can reach them -- it is that adoption was deliberately not
 * taken with it. Same deferral, and same owner, as `const/telemetry-redaction.ts` carries for its own copy of
 * this gap.
 *
 * IT ALSO DROPS DATA, which matters here in a way it does not at the projection's own call sites. `redactTelemetryPayload`
 * is a PUBLICATION projection built to bound a telemetry row, so it withholds object KEYS on three axes -- keys past
 * `TELEMETRY_REDACTION_MAX_OBJECT_KEYS` (128), key NAMES longer than `TELEMETRY_REDACTION_KEY_NAME_MAX_CHARS` (48), and names
 * failing `TELEMETRY_REDACTION_KEY_NAME_PATTERN` (identifier-shaped only, so an email or an id used as a map key goes) --
 * replacing each group with a COUNT marker (`<keys-truncated:N>`, `<dynamic-keys:N>`) rather than the entries. It also
 * coalesces a top-level `undefined` to `null`, so a fallback `console.error(undefined)` prints `null`.
 *
 * That is correct for a wire row and lossy for a DIAGNOSTIC line, which is the trade this fallback accepts: an
 * unconfigured host gets a bounded, scrubbed console line instead of an unbounded verbatim one, and a debugger reading
 * `<dynamic-keys:7>` should know seven keys existed rather than assume the object was empty. The cure is not to loosen
 * the projection -- it is to CONFIGURE the real loggers, which is what the header above already tells every entry point.
 *
 * An `Error` is projected from its `stack` (already `Name: message` plus frames) because the projection walks PLAIN
 * objects and would otherwise render a live Error as `<unknown>`, losing the whole diagnostic.
 */
const scrubFallbackArg = (arg: unknown): unknown => redactTelemetryPayload(arg instanceof Error ? (arg.stack ?? arg.message) : arg);

const consoleFor =
	(level: keyof SecureLogger) =>
	(...args: unknown[]): void => {
		console[level](...args.map(scrubFallbackArg));
	};

/**
 * Whether a diagnostic (debug / info) line may reach the console HERE. Always in a Worker or Node isolate, where the
 * console is the observability feed; in a document only when the operator has switched it on
 * (`BROWSER_DEBUG_LOG_STORAGE_KEY`). Read per call, so flipping the flag in devtools takes effect without a reload.
 */
function diagnosticsEnabled(): boolean {
	// `document`, NOT A `window` PROBE, and not the storage accessor's null either: the question is "is this a page",
	// and a page whose storage is unavailable (a sandboxed frame) must stay QUIET, where "no storage" read as "not a
	// browser" would turn every debug line on for exactly the visitor least able to switch them off.
	if (typeof document === 'undefined') return true;
	try {
		return documentLocalStorage()?.getItem(BROWSER_DEBUG_LOG_STORAGE_KEY) === '1';
	} catch {
		return false;
	}
}

const diagnosticConsoleFor =
	(level: keyof SecureLogger) =>
	(...args: unknown[]): void => {
		if (diagnosticsEnabled()) consoleFor(level)(...args);
	};

/** Console-backed SecureLogger fallback (value-pattern scrub only; no deep PII redaction). Debug and info are gated in a browser. */
const defaultSecureLogger: SecureLogger = {
	error: consoleFor(LOG_LEVELS.ERROR),
	warn: consoleFor(LOG_LEVELS.WARN),
	info: diagnosticConsoleFor(LOG_LEVELS.INFO),
	debug: diagnosticConsoleFor(LOG_LEVELS.DEBUG),
};

/** Console-backed ScopedLogger fallback: `[module]` prefix + bound-context trailing arg, mirroring the real scoped-logger shape. */
function defaultScopedLogger(moduleName: string, bound: Partial<StandardLogFields> = {}): ScopedLogger {
	const emitArgs = (args: unknown[]): unknown[] => [`[${moduleName}]`, ...args, ...(hasAnyKeys(bound) ? [bound] : [])];
	return {
		error: (...args: unknown[]) => defaultSecureLogger.error(...emitArgs(args)),
		warn: (...args: unknown[]) => defaultSecureLogger.warn(...emitArgs(args)),
		info: (...args: unknown[]) => defaultSecureLogger.info(...emitArgs(args)),
		debug: (...args: unknown[]) => defaultSecureLogger.debug(...emitArgs(args)),
		withContext: (fields: Partial<StandardLogFields>) => defaultScopedLogger(moduleName, { ...bound, ...fields }),
	};
}

/** Shape-stable failure envelope returned by unconfigured issue-logging fallbacks (the D1 issue mint did NOT happen). */
const UNCONFIGURED_ISSUE_RESULT = { success: false, error: UNCONFIGURED_SENTINEL } as const;

/** An unconfigured host cannot mint the D1 issue, so this surfaces the would-be issue through the scrubbed console logger -- never a silent no-op -- and returns the shape-stable failure envelope so callers see the mint did not happen. */
const unconfiguredIssueSink =
	(slot: string) =>
	(...args: unknown[]): Promise<typeof UNCONFIGURED_ISSUE_RESULT> => {
		defaultSecureLogger.error(`[${UNCONFIGURED_SENTINEL}] ${slot}:`, ...args);
		return Promise.resolve(UNCONFIGURED_ISSUE_RESULT);
	};

type RegistryLogActivityFn = (activity: Record<string, unknown>, context: LogActivityContextArg) => { id: string; trace_id: string };
type RegistryLogLevelFn = (
	source: string,
	message: string,
	options: { env?: Record<string, unknown> | null; context?: unknown; extra?: Record<string, unknown> },
) => { id: string; trace_id: string };

/** The UNCONFIGURED registry's id pair: both ids are the sentinel rather than a real id or a silent null, so a caller
 *  reading `{ id, trace_id }` stays shape-stable while the value says plainly that nothing was recorded. A FACTORY --
 *  the record is handed to the caller. */
const unconfiguredIdPair = (): { id: string; trace_id: string } => ({ id: UNCONFIGURED_SENTINEL, trace_id: UNCONFIGURED_SENTINEL });

const _registry: Record<string, unknown> = {
	// Fallbacks emit to console (never silent) and return sentinel ids so callers reading { id, trace_id } stay shape-stable.
	logError: (source: string, error: unknown): { id: string | null; trace_id: string | null } => {
		defaultSecureLogger.error(`[${UNCONFIGURED_SENTINEL}] ${source}:`, error);
		return { id: null, trace_id: null };
	},
	logActivity: ((activity) => {
		defaultSecureLogger.info(`[${UNCONFIGURED_SENTINEL}] activity:`, activity);
		return unconfiguredIdPair();
	}) satisfies RegistryLogActivityFn,
	logWarn: ((source, message) => {
		defaultSecureLogger.warn(`[${UNCONFIGURED_SENTINEL}] ${source}: ${message}`);
		return unconfiguredIdPair();
	}) satisfies RegistryLogLevelFn,

	secureLogger: defaultSecureLogger,
	scopedLogger: (moduleName: string): ScopedLogger => defaultScopedLogger(moduleName),

	// Mirrors the real wrapper surface in tools/issues/issue-logger-wrappers: issueLogger carries error + logError, and there is no `log` method.
	createIssueLogger: unconfiguredIssueSink('createIssueLogger'),
	issueLogger: {
		error: unconfiguredIssueSink('issueLogger.error'),
		logError: unconfiguredIssueSink('issueLogger.logError'),
	},
	insertIssueLogger: unconfiguredIssueSink('insertIssueLogger'),

	// Default no-op returns a SETTLED promise so callers never await undefined before an entry point injects flowBackedInsertIssue.
	insertIssue: () => Promise.resolve(),

	// Observability record builder: entry points inject the tools/observability/activity-emit impl; unconfigured direct-node scripts hit this sentinel default.
	buildActivityBase: (userId: string, _env: unknown, nowIso: string) => ({
		id: `${UNCONFIGURED_SENTINEL}-${userId}`,
		user_id: userId,
		created_at: nowIso,
		updated_at: nowIso,
		modifying_flow: UNCONFIGURED_SENTINEL,
		livemode: 0,
	}),
};

const getLogError = (): unknown => _registry.logError;

const getCreateIssueLogger = (): unknown => _registry.createIssueLogger;
export const getIssueLogger = (): unknown => _registry.issueLogger;
export const getInsertIssueLogger = (): unknown => _registry.insertIssueLogger;
export const getInsertIssue = (): unknown => _registry.insertIssue;

export const createIssueLogger = (...args: unknown[]): unknown => (getCreateIssueLogger() as (...a: unknown[]) => unknown)(...args);

export const logError = (...args: unknown[]): unknown => (getLogError() as (...a: unknown[]) => unknown)(...args);

/** PII-redacting logger; resolves the current registry slot per call, so import order vs configureRegistry is irrelevant. */
export const secureLogger: SecureLogger = {
	error: (...args: unknown[]) => (_registry.secureLogger as SecureLogger).error(...args),
	warn: (...args: unknown[]) => (_registry.secureLogger as SecureLogger).warn(...args),
	info: (...args: unknown[]) => (_registry.secureLogger as SecureLogger).info(...args),
	debug: (...args: unknown[]) => (_registry.secureLogger as SecureLogger).debug(...args),
};

/** Resolves the registry's scopedLogger factory per call, so module-top-level `scopedLogger('x')` is safe before configureRegistry. */
function lazyScopedLogger(moduleName: string, bound: Partial<StandardLogFields>): ScopedLogger {
	const resolve = (): ScopedLogger => {
		const logger = (_registry.scopedLogger as (name: string) => ScopedLogger)(moduleName);
		return hasAnyKeys(bound) ? logger.withContext(bound) : logger;
	};
	return {
		error: (...args: unknown[]) => resolve().error(...args),
		warn: (...args: unknown[]) => resolve().warn(...args),
		info: (...args: unknown[]) => resolve().info(...args),
		debug: (...args: unknown[]) => resolve().debug(...args),
		withContext: (fields: Partial<StandardLogFields>) => lazyScopedLogger(moduleName, { ...bound, ...fields }),
	};
}

/** Registry-backed drop-in for tools/observability/scoped-logger. */
export const scopedLogger = (moduleName: string): ScopedLogger => lazyScopedLogger(moduleName, {});

/** Sentinel id `defaultPropertyOpsLogger`'s arms answer with. Distinct from {@link UNCONFIGURED_SENTINEL}: this logger
 *  is not a fallback for a missing slot, it IS the property-ops logger, and it mints no correlation ids. */
const PROPERTY_OPS_SENTINEL_ID = 'default-logger';

/** The scoped-logger arm an emitted property-ops record rides. Taking it as a PARAMETER is what keeps level fidelity on
 *  the wire: the arms previously all called `log.error` and encoded the real level in the payload, so a warn and an info
 *  both left this process AS errors -- unfilterable downstream, and indistinguishable from a genuine property-ops
 *  failure. */
type PropertyOpsLevelEmit = (source: string, payload: Record<string, unknown>) => void;

type PropertyOpsLogMethod = (
	source: string,
	messageOrError: unknown,
	options: { env: Record<string, unknown> | null; extra?: Record<string, unknown> },
) => { id: string; trace_id: string };

/** Factory for the error/warn/info arms, which share identical structure and differ only in the level they emit at. */
function makePropertyOpsLogMethod(level: string, emit: PropertyOpsLevelEmit): PropertyOpsLogMethod {
	return (source, messageOrError, options) => {
		emit(source, { level, messageOrError, ...(options.extra ?? {}) });
		return { id: PROPERTY_OPS_SENTINEL_ID, trace_id: PROPERTY_OPS_SENTINEL_ID };
	};
}

/**
 * Default PropertyOpsLogger that logs to console instead of silently dropping. The property flows take it as an
 * injected dep; the PropertyDO and its handler leaves reach it directly.
 *
 * IT LIVES HERE, in the DI registry, and the home is a layer consequence rather than a preference. A `tools/` module
 * may be imported only by a flow leaf, and this logger's non-flow importers are the PropertyDO class and two of its
 * handler leaves -- but it cannot be a `utils/` leaf of its own either, because its whole body is `scopedLogger(...)`,
 * a utils value that `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids a utils sibling from importing. Homing it in the
 * leaf that ALREADY OWNS `scopedLogger` dissolves that edge instead of re-minting it: the dependency is module-local.
 * Its `logPropertyError` shim stays in `tools/`, where its only production consumer is a flow leaf.
 */
export const defaultPropertyOpsLogger: PropertyOpsLogger = {
	error: makePropertyOpsLogMethod('ERROR', (source, payload) => scopedLogger(LOGGER_TAGS.PROPERTY_OPS).error(source, payload)),
	warn: makePropertyOpsLogMethod('WARN', (source, payload) => scopedLogger(LOGGER_TAGS.PROPERTY_OPS).warn(source, payload)),
	info: makePropertyOpsLogMethod('INFO', (source, payload) => scopedLogger(LOGGER_TAGS.PROPERTY_OPS).info(source, payload)),
	errorAndReport(source, error, options) {
		// The scoped-logger line alone: no consumer reads a report envelope, so this arm files no issue and emits none.
		scopedLogger(LOGGER_TAGS.PROPERTY_OPS).error(source, { level: 'ERROR+REPORT', error, ...(options.extra ?? {}) });
		return Promise.resolve();
	},
};

/** Call once at application startup to inject the real implementations. Declared BELOW the logger wrappers its
 * identity guard compares against. */
export function configureRegistry(deps: Record<string, unknown> = {}): void {
	// Identity guard: supplying this module's own lazy wrapper for a logger slot points the slot at itself, and the
	// first log call then recurses infinitely ("Maximum call stack size exceeded" -- witnessed killing the full
	// compliance scan when the dispatch heartbeat fired). Fail loud at configure time instead.
	if (deps.secureLogger === secureLogger || deps.scopedLogger === scopedLogger) {
		throw new Error(
			'configureRegistry: a logger slot was supplied with the registry wrapper itself -- import the real implementation (tools/observability) at the composition root, never the @federa/utils/deps/registry re-resolver.',
		);
	}
	Object.assign(_registry, deps);
}

export const logActivity: RegistryLogActivityFn = (activity, context) =>
	(_registry.logActivity as RegistryLogActivityFn)(activity, context);

export const logWarn: RegistryLogLevelFn = (source, message, options) =>
	(_registry.logWarn as RegistryLogLevelFn)(source, message, options);

/** Returns the SENTINEL base until an entry injects the real activity-emit impl. */
export const buildActivityBase = (...args: unknown[]): ActivityBase =>
	(_registry.buildActivityBase as (...a: unknown[]) => ActivityBase)(...args);
